"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.usePickupPoints = exports.PickupPointsProvider = void 0;
var _auth = require("@ori/auth");
var _configurationManager = require("@ori/configuration-manager");
var _environment = require("@ori/environment");
var _fetching = require("@ori/fetching");
var _infinite = require("@ori/fetching/infinite");
var _i18n = require("@ori/i18n");
var _presentationUtils = require("@ori/presentation-utils");
var _shoppingContext = require("@ori/shopping-context");
var _react = require("react");
var _useDebounce = require("use-debounce");
var _fetchPickupPoints = require("../api/fetchPickupPoints");
var _constants = require("../constants");
var _SearchConfigurationProvider = require("../containers/SearchConfigurationProvider");
var _checkoutDeliveryContext = require("./checkoutDeliveryContext");
var _jsxRuntime = require("react/jsx-runtime");
const usePickupPointsApi = (enabled, perPage = 7) => {
  const {
    graphQlNetUrl
  } = (0, _configurationManager.useServiceUrls)(['graphQlNet']);
  const {
    language
  } = (0, _i18n.useLanguage)();
  const {
    tenant
  } = (0, _environment.useEnvironment)();
  const {
    token
  } = (0, _auth.useToken)();
  const {
    customerId
  } = (0, _checkoutDeliveryContext.useCheckoutDeliveryContext)();
  const sessionId = (0, _shoppingContext.useSessionId)();
  const {
    location,
    zoom
  } = (0, _SearchConfigurationProvider.useSearchConfiguration)().viewSettings;

  // Search term handling
  const [searchTerm, setSearchTerm] = (0, _react.useState)('');
  const [searchDebounced] = (0, _useDebounce.useDebounce)(searchTerm.length < 3 ? '' : searchTerm, 1500);
  const [searchUsed, setSearchUsed] = (0, _react.useState)(false);

  // Reset searchUsed when location or zoom changes
  (0, _react.useEffect)(() => setSearchUsed(false), [location, zoom]);

  // Mark search to be used when search term is set
  (0, _react.useEffect)(() => setSearchUsed(!!searchDebounced), [searchDebounced]);

  // Geocoding search params, remove after backend API fix
  const {
    formattedToken
  } = (0, _auth.useToken)();
  const {
    credentials,
    mapProvider,
    setViewSettings,
    zoomAfterSelection
  } = (0, _SearchConfigurationProvider.useSearchConfiguration)();
  // Geocoding search params end

  const headers = (0, _react.useMemo)(() => (0, _fetching.getHeaders)({
    language,
    customerId,
    sourceAppName: _constants.APP_NAME,
    tenant,
    token
  }), [language, customerId, tenant, token]);
  const {
    data,
    ...response
  } = (0, _infinite.useSWRInfinite)((pageIndex, prev) => {
    const skip = pageIndex * perPage;
    if (!enabled || prev !== null && skip > prev.totalRecords) {
      return null;
    }
    return {
      headers,
      graphQlNetUrl,
      params: {
        searchTerm: searchUsed ? searchDebounced : undefined,
        ...location,
        sessionId,
        top: perPage,
        skip,
        radius: _constants.SEARCH_RADIUS
      },
      // Geocoding search params, remove after backend API fix
      credentials,
      mapProvider,
      language,
      formattedToken,
      tenant
    };
  }, _fetchPickupPoints.fetchPickupPoints, {
    ...(0, _presentationUtils.getRetryErrorSwrInfiniteOptions)(),
    revalidateFirstPage: false
  });
  const pickupPoints = (0, _react.useMemo)(() => (data ?? []).flatMap(page => page.pickupPoints), [data]);

  // Focus map on found pickup points
  (0, _react.useEffect)(() => {
    const {
      latitude,
      longitude
    } = pickupPoints[0] ?? {};
    if (!searchUsed || latitude === undefined || longitude === undefined) {
      return;
    }

    // If the location is not the same as the one in the map, set the new location
    if (Math.abs(location.latitude - latitude) > 0.0001 || Math.abs(location.longitude - longitude) > 0.0001) {
      setViewSettings({
        location: {
          latitude,
          longitude
        },
        zoom: zoomAfterSelection
      });
    }
  }, [pickupPoints, searchUsed, location, zoom, setViewSettings, zoomAfterSelection]);

  // Infinite scroll handling
  const hasMore = (data?.[0]?.totalRecords ?? 0) !== pickupPoints.length;
  const loadMore = async () => {
    if (response.isValidating || !hasMore) {
      return;
    }
    await response.setSize(response.error ? response.size : response.size + 1);
  };
  return {
    pickupPoints,
    ...response,
    hasMore,
    loadMore,
    searchTerm,
    setSearchTerm
  };
};
const pickupPointsContext = /*#__PURE__*/(0, _react.createContext)(undefined);
const PickupPointsProvider = ({
  enabled,
  children
}) => /*#__PURE__*/(0, _jsxRuntime.jsx)(pickupPointsContext.Provider, {
  value: usePickupPointsApi(enabled),
  children: children
});
exports.PickupPointsProvider = PickupPointsProvider;
const usePickupPoints = () => {
  const ctx = (0, _react.useContext)(pickupPointsContext);
  if (!ctx) {
    throw new Error('usePickupPoints must be used within a PickupPointsProvider');
  }
  return ctx;
};
exports.usePickupPoints = usePickupPoints;